import PropTypes from 'prop-types';
import Input from '@/ui/Form/Input.jsx';
import { formatCLP } from '@/lib/format.js';
import { amountText, parsePesos, rowSubtotal } from '@/lib/priceBreakdown.js';

// "2 × $45.000 + $5.000 de recargo = $95.000", only the parts that apply, or
// "Subtotal $45.000" when there is nothing to add.
function subtotalText(row, typed) {
  const unitAmount = parsePesos(typed.unitAmount);
  const surcharge = parsePesos(typed.surcharge, { optional: true });
  const text = amountText({ units: row.units, unitAmount, surcharge });
  return text === formatCLP(unitAmount * row.units + surcharge)
    ? `Subtotal ${text}`
    : text;
}

// The price of an offer for a request that names its recipients: an amount per
// recipient (per representative for a company) and an optional surcharge,
// with each subtotal and the total as they are typed. Used by the receptor's
// detail and by /oferta, which keep the values and validate on submit.
export default function PriceBreakdownFields({
  idPrefix,
  rows,
  values,
  errors = {},
  totalError,
  onChange,
  onFormat,
  disabled = false,
}) {
  let total = 0;
  let complete = true;
  for (const row of rows) {
    const sub = rowSubtotal(row, values[row.recipientIndex]);
    if (sub == null) complete = false;
    else total += sub;
  }
  const totalId = `${idPrefix}-total`;
  const totalErrorId = `${idPrefix}-total-error`;
  return (
    <fieldset className="sold-breakdown" disabled={disabled}>
      <legend>Precio por destinatario</legend>
      {rows.map((row) => {
        const i = row.recipientIndex;
        const typed = values[i] ?? {};
        const rowErrors = errors[i] ?? {};
        const sub = rowSubtotal(row, typed);
        const unitLabel =
          row.perRepresentative && row.units > 1
            ? 'Monto por representante'
            : 'Monto';
        const field = (key, label, placeholder) => {
          const id = `${idPrefix}-${i}-${key}`;
          const errorId = `${id}-error`;
          return (
            <div className="sold-control">
              <label htmlFor={id}>{label}</label>
              <Input
                id={id}
                variant="mono"
                inputMode="numeric"
                autoComplete="off"
                placeholder={placeholder}
                value={typed[key] ?? ''}
                aria-invalid={Boolean(rowErrors[key])}
                aria-describedby={rowErrors[key] ? errorId : undefined}
                onChange={(e) => onChange(i, key, e.target.value)}
                // 45000 reads as 45.000 once typed, like the single price.
                onBlur={(e) => {
                  const n = parsePesos(e.target.value);
                  const formatted = n ? n.toLocaleString('es-CL') : null;
                  // Formatting is not an edit: it goes through onFormat, which
                  // keeps the form's errors, since the amount is the same.
                  if (formatted && formatted !== e.target.value)
                    (onFormat ?? onChange)(i, key, formatted);
                }}
              />
              {rowErrors[key] ? (
                <p id={errorId} className="sold-form-error">
                  {rowErrors[key]}
                </p>
              ) : null}
            </div>
          );
        };
        return (
          <div
            key={i}
            className="sold-breakdown__row"
            role="group"
            aria-label={`Destinatario ${i + 1}`}
          >
            <p className="sold-breakdown__who">
              <b>Destinatario {i + 1}</b> · {row.who}
            </p>
            <div className="sold-breakdown__fields">
              {field('unitAmount', unitLabel, '45.000')}
              {field('surcharge', 'Recargo (opcional)', '0')}
            </div>
            {sub != null ? (
              <p className="sold-breakdown__sub">{subtotalText(row, typed)}</p>
            ) : null}
          </div>
        );
      })}
      <p id={totalId} className="sold-breakdown__total" aria-live="polite">
        {complete ? `Total ${formatCLP(total)}` : 'Total por calcular'}
      </p>
      {totalError ? (
        <p id={totalErrorId} className="sold-form-error" role="alert">
          {totalError}
        </p>
      ) : null}
    </fieldset>
  );
}

PriceBreakdownFields.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      recipientIndex: PropTypes.number.isRequired,
      units: PropTypes.number.isRequired,
      who: PropTypes.string.isRequired,
      perRepresentative: PropTypes.bool,
    }),
  ).isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  totalError: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  // Rewrites a value without changing its amount (thousands on blur).
  onFormat: PropTypes.func,
  disabled: PropTypes.bool,
};
